import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ButtonFilled from "../../../../components/FormElements/ButtonFilled";
import Modal from "../../../../portals/Modal";
import TextField from "../../../../components/FormElements/TextField";
import ButtonOutlined from "../../../../components/FormElements/ButtonOutlined";
import {useFormik} from "formik";
import * as Yup from "yup";
import { store } from "react-notifications-component";
import instance from "../../../../api";
import PageHeader from "../../../../components/PageHeader";
import QuestionTooltip from "../../../../components/QuestionTooltip";
import styles from './profileBonusGradiation.module.scss';
import VideoModal from "../../../../components/VideoModal";
import YOUTUBE_LINKS from "../../../../config/youtubeLinks";
import GroupConfigWarning from "../../../../components/GroupConfigWarning";
import WalletCardPreview from "./WalletCardPreview";


const formatExpire = (ms) => {
    if (!ms) return '';
    const d = new Date(ms);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

export default function ProfileBonusGradation() {
    const { t } = useTranslation();
    const { info }  = useSelector(s => s.branchReducer);
    const [errorModal, setErrorModal] = useState(false);
    const [cashbacks, setCashbacks] = useState([]);
    const [deleting, setDeleting] = useState(false);
    const [designs, setDesigns] = useState({});
    const [designBusy, setDesignBusy] = useState({});
    const [livePhone, setLivePhone] = useState('');
    const [liveBusy, setLiveBusy] = useState(false);
    const [liveCustomer, setLiveCustomer] = useState(null);

    const bonusName = (info?.configuration?.private_cashback?.bonus_name) || 'Бонусы';
    const barcodeType = info?.configuration?.wallet?.type;
    const logo = info?.icon;

    const resolveTierByStatus = (status) => {
        if (!status) return null;
        const pctMatch = status.match(/(\d+)%\s*$/);
        const pct = pctMatch ? Number(pctMatch[1]) : null;
        const title = status.replace(/\s*\d+%\s*$/, '').trim();
        if (title) {
            const byTitle = cashbacks.find(c => (c.title || '').trim() === title);
            if (byTitle) return byTitle;
        }
        if (pct != null) {
            const byPct = cashbacks.find(c => Number(c.extra_cashback) === pct);
            if (byPct) return byPct;
        }
        return null;
    };

    const onFindLiveCustomer = async (e) => {
        e.preventDefault();
        const cleanPhone = livePhone.replace(/\D/g, '');
        if (!cleanPhone) return;
        setLiveBusy(true);
        try {
            const found = await instance().get(`/promoters/find`, { params: { phone: cleanPhone, branch_id: info.id } }).then(r => r.data);
            const userId = found.data.id;
            const detail = await instance().get(`/branches/group/${info.group_id}/customers/${userId}`).then(r => r.data);
            setLiveCustomer(detail.data);
        } catch (err) {
            setLiveCustomer(null);
            notifyError(err);
        } finally {
            setLiveBusy(false);
        }
    };

    const notifyError = (e) => {
        const res = e.response ? e.response.data : null;
        store.addNotification({
            title: `Error code: ${res ? res.code : 'NULL'}`,
            message: `${res ? res.message : t('Notify.notRespond')}`,
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: { duration: 5000, onScreen: true }
        });
    };

    const setDesignField = (id, field, value) => {
        setDesigns(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
    };

    const onSaveColors = (c) => async () => {
        setDesignBusy(prev => ({ ...prev, [c.id]: true }));
        try {
            const d = designs[c.id] || {};
            const res = await instance().put(`/branches/${info.id}/gradation/${c.id}/design`, {
                bg_color: d.bg_color || '',
                text_color: d.text_color || ''
            }).then(r => r.data);
            setCashbacks(res.data);
        } catch (e) {
            notifyError(e);
        } finally {
            setDesignBusy(prev => ({ ...prev, [c.id]: false }));
        }
    };

    const onUploadImage = (c) => async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setDesignBusy(prev => ({ ...prev, [c.id]: true }));
        try {
            const form = new FormData();
            form.append('image', file);
            const res = await instance().post(`/branches/${info.id}/gradation/${c.id}/image`, form).then(r => r.data);
            setCashbacks(res.data);
        } catch (err) {
            notifyError(err);
        } finally {
            setDesignBusy(prev => ({ ...prev, [c.id]: false }));
        }
    };

    const onDeleteImage = (c) => async () => {
        setDesignBusy(prev => ({ ...prev, [c.id]: true }));
        try {
            const res = await instance().delete(`/branches/${info.id}/gradation/${c.id}/image`).then(r => r.data);
            setCashbacks(res.data);
        } catch (e) {
            notifyError(e);
        } finally {
            setDesignBusy(prev => ({ ...prev, [c.id]: false }));
        }
    };

    const formik = useFormik({
        initialValues: {
            new_cashback: '',
            amount: '',
            title: ''
        },
        validationSchema: Yup.object({
            new_cashback: Yup.string().required(t('Form.fillField')),
            amount: Yup.string().required(t('Form.fillField')),
        }),
        onSubmit: (async (values, { resetForm, setSubmitting }) => {
            setSubmitting(true);

            try {
                const res = await instance().post(`/branches/${info.id}/gradation/cashback`, {
                    amount: Number(values.amount),
                    new_cashback: Number(values.new_cashback),
                    title: values.title
                }).then(res => res.data);

                setCashbacks(res.data);
                resetForm();
            } catch (e) {
                const res = e.response ? e.response.data : null;

                store.addNotification({
                    title: `Error code: ${res ? res.code : 'NULL'}`,
                    message: `${res ? res.message : t('Notify.notRespond')}`,
                    type: "danger",
                    insert: "top",
                    container: "top-right",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 5000,
                        onScreen: true
                    }
                });
            } finally {
                setSubmitting(false);
            }
        })
    });

    useEffect(() => {
        if (info && info.rule.gradation_cashbacks) {
            setCashbacks(info.rule.gradation_cashbacks);
        }
        if (info && (!info.rule.gradation_cashbacks || !info.rule.gradation_cashbacks.length)) {
            setCashbacks([]);
        }
    }, [info]);

    useEffect(() => {
        const next = {};
        cashbacks.forEach(c => {
            next[c.id] = {
                bg_color: c.bg_color || '#edf3ed',
                text_color: c.text_color || '#000000'
            };
        });
        setDesigns(next);
    }, [cashbacks]);

    const onDeleteCashback = (c) => async (e) => {
        try {
            setDeleting(true);
            await instance().delete(`/branches/${info.id}/gradation/${c.id}`);
            setCashbacks((prev) => prev.filter(p => p.id !== c.id));
        } catch (e) {
            const res = e.response ? e.response.data : null;

            store.addNotification({
                title: `Error code: ${res ? res.code : 'NULL'}`,
                message: `${res ? res.message : t('Notify.notRespond')}`,
                type: "danger",
                insert: "top",
                container: "top-right",
                animationIn: ["animate__animated", "animate__fadeIn"],
                animationOut: ["animate__animated", "animate__fadeOut"],
                dismiss: {
                    duration: 5000,
                    onScreen: true
                }
            });
        } finally {
            setDeleting(false);
        }
    }

    return (
        <>
            <PageHeader title={t('bonuses')}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
                <div style={{ marginLeft: '50px' }}>
                  <QuestionTooltip text={t('NSCumulative.info')} />
                </div>
                <VideoModal url={YOUTUBE_LINKS.BonusGradation} />
              </div>

            </PageHeader>
            <div className={styles.container}>
                <GroupConfigWarning branch={info}>
                    {t('groupWarning.gradation', { count: info?.group_branch_count ?? 0 })}
                </GroupConfigWarning>
                {info?.configuration?.whatsapp_provider?.provider !== 'green_api' && (
                    <div style={{ background: '#FFF4E5', border: '1px solid #FFB74D', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#7A4F01', fontSize: 14 }}>
                        ⚠️ {t('NSCumulative.greenApiWarning')}
                    </div>
                )}
                <form className={styles.form} onSubmit={formik.handleSubmit}>
                    <div className={styles.block}>
                        <TextField
                            label={t('NSCumulative.new')}
                            placeholder="8%"
                            disabled={formik.isSubmitting}
                            width="160px"

                            value={formik.values.new_cashback}
                            error={formik.touched.new_cashback && formik.errors.new_cashback}
                            onChange={(e) => isFinite(e.target.value) && +e.target.value <= 100 && formik.setFieldValue('new_cashback', e.target.value)}
                        />
                        <span className={styles['block__right-text']}>{t('NSCumulative.total')}</span>
                        <p className={styles['block__text']}>{t('NSCumulative.amount')}</p>
                    </div>
                    <div className={styles.block}>
                        <TextField
                            label={t('sum')}
                            placeholder="10 000"
                            disabled={formik.isSubmitting}
                            width="160px"

                            value={formik.values.amount}
                            error={formik.touched.amount && formik.errors.amount}
                            onChange={(e) => isFinite(e.target.value) && formik.setFieldValue('amount', e.target.value)}
                        />
                        <p className={styles['block__text']}>{t('NSCumulative.accumulation')}</p>
                    </div>
                    <div className={styles.block}>
                        <TextField
                            label={t('title')}
                            placeholder="Gold"
                            disabled={formik.isSubmitting}
                            width="250px"

                            value={formik.values.title}
                            onChange={(e) => formik.setFieldValue('title', e.target.value)}
                        />
                        <p className={styles['block__text']}>{t('NSCumulative.accumulation')}</p>
                    </div>
                    <ButtonOutlined variant="green" color="black" type="submit" disabled={formik.isSubmitting}>{t('add')}</ButtonOutlined>
                </form>
                <table className="table">
                    <thead>
                        <tr>
                            <th>№</th>
                            <th>{t('NSCumulative.title')}</th>
                            <th>{t('NSCumulative.bonus')}</th>
                            <th>{t('NSCumulative.purchases')}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                    {!!cashbacks.length ? cashbacks.map((c, i) => (
                        <tr key={c.id}>
                            <td>{i + 1}</td>
                            <td>{c.title}</td>
                            <td>{c.extra_cashback}</td>
                            <td>{c.amount}</td>
                            <td>
                                <button style={{ marginLeft: 'auto', marginRight: '16px' }} className="btn-trash" type="button" onClick={onDeleteCashback(c, i)} disabled={deleting}>
                                    <i>
                                        <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M11.4219 1.875H8.84375V1.40625C8.84375 0.63085 8.2129 0 7.4375 0H5.5625C4.7871 0 4.15625 0.63085 4.15625 1.40625V1.875H1.57812C0.931953 1.875 0.40625 2.4007 0.40625 3.04688V4.6875C0.40625 4.94637 0.616133 5.15625 0.875 5.15625H1.13117L1.53614 13.6606C1.57191 14.4117 2.18891 15 2.94078 15H10.0592C10.8111 15 11.4281 14.4117 11.4639 13.6606L11.8688 5.15625H12.125C12.3839 5.15625 12.5938 4.94637 12.5938 4.6875V3.04688C12.5938 2.4007 12.068 1.875 11.4219 1.875ZM5.09375 1.40625C5.09375 1.14779 5.30404 0.9375 5.5625 0.9375H7.4375C7.69596 0.9375 7.90625 1.14779 7.90625 1.40625V1.875H5.09375V1.40625ZM1.34375 3.04688C1.34375 2.91765 1.4489 2.8125 1.57812 2.8125H11.4219C11.5511 2.8125 11.6562 2.91765 11.6562 3.04688V4.21875C11.5118 4.21875 1.94237 4.21875 1.34375 4.21875V3.04688ZM10.5274 13.616C10.5155 13.8664 10.3098 14.0625 10.0592 14.0625H2.94078C2.69015 14.0625 2.48448 13.8664 2.47259 13.616L2.06973 5.15625H10.9303L10.5274 13.616Z" fill="white"/>
                                            <path d="M6.5 13.125C6.75887 13.125 6.96875 12.9151 6.96875 12.6562V6.5625C6.96875 6.30363 6.75887 6.09375 6.5 6.09375C6.24113 6.09375 6.03125 6.30363 6.03125 6.5625V12.6562C6.03125 12.9151 6.2411 13.125 6.5 13.125Z" fill="white"/>
                                            <path d="M8.84375 13.125C9.10262 13.125 9.3125 12.9151 9.3125 12.6562V6.5625C9.3125 6.30363 9.10262 6.09375 8.84375 6.09375C8.58488 6.09375 8.375 6.30363 8.375 6.5625V12.6562C8.375 12.9151 8.58485 13.125 8.84375 13.125Z" fill="white"/>
                                            <path d="M4.15625 13.125C4.41512 13.125 4.625 12.9151 4.625 12.6562V6.5625C4.625 6.30363 4.41512 6.09375 4.15625 6.09375C3.89738 6.09375 3.6875 6.30363 3.6875 6.5625V12.6562C3.6875 12.9151 3.89735 13.125 4.15625 13.125Z" fill="white"/>
                                        </svg>
                                    </i>
                                    <span>{t('delete')}</span>
                                </button>
                            </td>
                        </tr>
                    )) : (
                        <tr>
                            <td>-</td>
                            <td>-</td>
                            <td>-</td>
                            <td></td>
                        </tr>
                    )}

                    </tbody>
                </table>

                {!!cashbacks.length && (
                    <div style={{ marginTop: 32 }}>
                        <h3 style={{ marginBottom: 16 }}>Дизайн карты по уровням</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                            {cashbacks.map((c) => {
                                const d = designs[c.id] || {};
                                const stripUrl = c.strip_image && c.strip_image.url;
                                const busy = !!designBusy[c.id];
                                return (
                                    <div key={c.id} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, width: 280 }}>
                                        <div style={{ fontWeight: 600, marginBottom: 12 }}>{c.title || `#${c.amount}`}</div>
                                        {stripUrl ? (
                                            <img src={stripUrl} alt="" style={{ width: '100%', height: 96, objectFit: 'cover', borderRadius: 8, marginBottom: 12, display: 'block' }} />
                                        ) : (
                                            <div style={{ borderRadius: 8, marginBottom: 12, background: d.bg_color || '#edf3ed', color: d.text_color || '#000', height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <span style={{ fontSize: 12, opacity: 0.6 }}>Картинка не загружена</span>
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                            <span style={{ width: 90 }}>Фон</span>
                                            <input type="color" value={d.bg_color || '#edf3ed'} disabled={busy} onChange={(e) => setDesignField(c.id, 'bg_color', e.target.value)} />
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                            <span style={{ width: 90 }}>Текст</span>
                                            <input type="color" value={d.text_color || '#000000'} disabled={busy} onChange={(e) => setDesignField(c.id, 'text_color', e.target.value)} />
                                        </div>
                                        <ButtonOutlined variant="green" color="black" type="button" disabled={busy} onClick={onSaveColors(c)}>
                                            {t('save')}
                                        </ButtonOutlined>
                                        <div style={{ marginTop: 12 }}>
                                            <label style={{ display: 'inline-block', cursor: 'pointer', textDecoration: 'underline' }}>
                                                Загрузить картинку
                                                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={busy} onChange={onUploadImage(c)} />
                                            </label>
                                        </div>
                                        {stripUrl && (
                                            <button type="button" className="btn-trash" style={{ marginTop: 8 }} disabled={busy} onClick={onDeleteImage(c)}>
                                                <span>{t('delete')}</span>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <h3 style={{ margin: '32px 0 16px' }}>Превью карты по уровням</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                            {cashbacks.map((c) => {
                                const d = designs[c.id] || {};
                                return (
                                    <WalletCardPreview
                                        key={c.id}
                                        bonusName={bonusName}
                                        logo={logo}
                                        stripUrl={c.strip_image && c.strip_image.url}
                                        bgColor={d.bg_color || c.bg_color || '#edf3ed'}
                                        textColor={d.text_color || c.text_color || '#000000'}
                                        balance="1 500"
                                        clientName="Имя клиента"
                                        cashback={c.extra_cashback}
                                        tier={c.title}
                                        barcodeType={barcodeType}
                                        code="000000000"
                                    />
                                );
                            })}
                        </div>

                        <h3 style={{ margin: '32px 0 8px' }}>Превью по клиенту</h3>
                        <p style={{ marginBottom: 16, opacity: 0.7 }}>Введите номер телефона клиента, чтобы увидеть его карту с актуальным балансом и уровнем.</p>
                        <form onSubmit={onFindLiveCustomer} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
                            <TextField
                                label="Номер телефона"
                                placeholder="+7 700 000 00 00"
                                width="240px"
                                value={livePhone}
                                disabled={liveBusy}
                                onChange={(e) => setLivePhone(e.target.value)}
                            />
                            <ButtonOutlined variant="green" color="black" type="submit" disabled={liveBusy}>
                                {liveBusy ? '...' : 'Показать'}
                            </ButtonOutlined>
                        </form>
                        {liveCustomer && (() => {
                            const tier = resolveTierByStatus(liveCustomer.gradation_status);
                            const d = tier ? (designs[tier.id] || {}) : {};
                            const pctMatch = (liveCustomer.gradation_status || '').match(/(\d+)%\s*$/);
                            const cashback = tier ? tier.extra_cashback : (pctMatch ? Number(pctMatch[1]) : null);
                            return (
                                <WalletCardPreview
                                    bonusName={bonusName}
                                    logo={logo}
                                    stripUrl={tier && tier.strip_image && tier.strip_image.url}
                                    bgColor={d.bg_color || (tier && tier.bg_color) || '#edf3ed'}
                                    textColor={d.text_color || (tier && tier.text_color) || '#000000'}
                                    balance={liveCustomer.bonuses != null ? String(liveCustomer.bonuses) : '0'}
                                    clientName={[liveCustomer.first_name, liveCustomer.last_name].filter(Boolean).join(' ') || 'Клиент'}
                                    cashback={cashback}
                                    tier={tier && tier.title}
                                    expire={formatExpire(liveCustomer.bonuses_expire)}
                                    barcodeType={barcodeType}
                                    code={String(liveCustomer.id || '')}
                                />
                            );
                        })()}
                    </div>
                )}
            </div>

            <Modal
                open={errorModal} size="small"
                onClickClose={() => setErrorModal(false)}>
                <div className={styles.modal}>
                    <p>
                        {t('optionForbidden')} <br/>
                        {t('touchModerator')}
                    </p>
                    <div>
                        <ButtonFilled
                            onClick={() => setErrorModal(false)}
                        >{t('close')}</ButtonFilled>
                    </div>
                </div>
            </Modal>
        </>
    )
}
